import { isolatedDialog, isolatedHeader } from 'Addons/fitmentSearch/isolatedKeys';
import { vehicleLandingFacetField } from 'Addons/fitmentSearch/vehicleLandingFacetField';
import { dialogClosed, dialogOpened } from 'Core/actions/dialog';
import {
  clearVehicleDialogRequest,
  clearVerifyFitmentRequest,
  discardVehicleValue,
  forceVehicleSelection,
  garageCleanupRequested,
  removalFromGarageRequested,
  replaceVehicleValue,
  replaceVerifyFitmentVehicleValue,
  sendInitFitmentSearchRequest,
  sendVehicleDialogRequest,
  sendVerifyFitmentRequest,
  setFitmentSearchResponse,
  setGarageRequested,
  setVehicleDialogSelection,
  setVerifyFitmentFacets,
  setVerifyFitmentProduct,
  setVerifyFitmentProductId,
  vehicleDiscardingRequested,
  vehicleSelected,
} from 'Core/actions/fitmentSearch/index';
import { replaceInHistory, replaceLocation } from 'Core/actions/redirect';
import { discard, resetRequest } from 'Core/actions/request';
import { setResponse, setResponseExtra } from 'Core/actions/response';
import { type EpicArg, epicFromHandlers } from 'Core/epics/common';
import { tryResponseRedirect } from 'Core/epics/redirect';
import {
  createAllRequiredFieldsHaveSelectionSelector,
  createFitmentSearchExtraFieldsSelector,
  createFitmentSearchRequestSelectionSelector,
  createFitmentSearchResponseFacetsSelector,
  createFitmentSearchSelectionChangedSelector as createSelectionChangedSelector,
  createRequiredFitmentSearchFieldsSelector,
  createResponseVehicleSelector,
  fitmentSearchFieldsSelector,
  garageDataSelector,
  isSpecifyDialogOpenedSelector,
  isVehicleSelectionForcedSelector,
  selectedVehicleSelector,
  vehicleInexactSelector,
  verifyFitmentProductIdSelector,
  verifyFitmentRequestSelectionSelector,
} from 'Core/selectors/fitmentSearch/index';
import { createFilteredResponseSelectionSelector } from 'Core/selectors/search';
import { logRuntimeError } from 'Diagnostics/diagnostics';
import { FacetCollection, Vehicle } from 'Models';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import productConfig from 'Models/uiConfig/productConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { getUriFromRequest } from 'Modules/converter/index';
import { abortControllerCreator, saveCustomerGarage, search } from 'Modules/serverApi/index';
import { VariantsMode } from 'Modules/serverApi/types';

const createDialogAbortController = abortControllerCreator();
const createHeaderAbortController = abortControllerCreator();
const createVerifyAbortController = abortControllerCreator();

export default epicFromHandlers(
  {
    [garageCleanupRequested.type]: (arg) => {
      sendRequestsIfNeeded(arg);
      onGarageUpdated(arg);
    },
    [setGarageRequested.type]: (arg) => {
      const {
        dispatch,
        action: {
          payload: { vehicles, selectVehicle },
        },
      } = arg;

      if (selectVehicle) {
        dispatch(vehicleSelected(vehicles.last, { isVehicleAutoSelected: true }));
      }

      onGarageUpdated(arg);
    },
    [removalFromGarageRequested.type]: (arg) => {
      if (arg.action.meta.wasSelected) {
        sendRequestsIfNeeded(arg);
      }

      onGarageUpdated(arg);
    },
    [dialogOpened.type]: (arg) => {
      const {
        state,
        dispatch,
        action: {
          payload,
          meta: { vehicleWidgetIncluded, withCurrentVehicle },
        },
      } = arg;
      if ((payload !== 'VehicleWidgetDialog' && !vehicleWidgetIncluded) || vehicleInexactSelector(state)) {
        return;
      }
      if (withCurrentVehicle) {
        const { selection } = selectedVehicleSelector(state);
        dispatch(setVehicleDialogSelection(selection, withCurrentVehicle));
      } else {
        sendIsolatedRequest(arg, isolatedDialog);
      }
    },
    [clearVehicleDialogRequest.type]: (arg) => sendIsolatedRequest(arg, isolatedDialog),
    [sendVehicleDialogRequest.type]: (arg) => sendIsolatedRequest(arg, isolatedDialog),
    [setVehicleDialogSelection.type]: (arg) => sendIsolatedRequest(arg, isolatedDialog),
    [clearVerifyFitmentRequest.type]: sendVerifyFitmentRequestFunc,
    [sendVerifyFitmentRequest.type]: sendVerifyFitmentRequestFunc,
    [replaceVerifyFitmentVehicleValue.type]: sendVerifyFitmentRequestFunc,
    [sendInitFitmentSearchRequest.type]: (arg) => sendIsolatedRequest(arg, isolatedHeader, true),
    [setFitmentSearchResponse.type]: tryAutoSelectVehicle,
    [setResponse.type]: (arg) => {
      syncUrlWithVehicle(arg);
      return tryAutoSelectVehicle(arg); // do return because of the test
    },
    [replaceVehicleValue.type]: (arg) => {
      const {
        state,
        action: {
          meta: {
            isolatedKey,
            responseMeta: { isVehicleSelectionIsolated },
          },
        },
      } = arg;

      if (isolatedKey && createSelectionChangedSelector(isolatedKey)(state)) {
        sendIsolatedRequest(arg, isolatedKey);
      } else if (isVehicleSelectionIsolated) {
        sendIsolatedRequest(arg, isolatedKey);
      }
    },
    [vehicleDiscardingRequested.type]: (arg) => {
      const { isInDialog, globalVehicleDiscardEnabled } = arg.action.meta;

      if (isInDialog) {
        sendIsolatedRequest(arg, isolatedDialog);
      }

      if (!isInDialog || globalVehicleDiscardEnabled) {
        sendRequestsIfNeeded(arg);
      }

      onGarageUpdated(arg);
    },
    [discardVehicleValue.type]: (arg) => {
      const { isInDialog } = arg.action.meta;

      if (isInDialog) {
        sendIsolatedRequest(arg, isolatedDialog);
      }
    },
    [vehicleSelected.type]: (arg) => {
      if (arg.action.meta.sendIsolatedRequest) {
        sendRequestsIfNeeded(arg);
      }

      onGarageUpdated(arg);
    },
    [forceVehicleSelection.type]: (arg) => sendIsolatedRequest(arg, isolatedDialog),
    [discard.type]: ({
      action: {
        payload: { value: { field } = {} },
      },
    }) => {
      if (field === vehicleLandingFacetField) {
        return vehicleDiscardingRequested({ mayDiscardValue: false });
      }
    },
    [resetRequest.type]: ({ state, action: { payload: { discardVehicle } = {} } }) => {
      const selection = createFilteredResponseSelectionSelector()(state);
      const isVehicleLandingFacetWasSelected = selection.some((s) => s.field === vehicleLandingFacetField);

      if (discardVehicle && isVehicleLandingFacetWasSelected) {
        return vehicleDiscardingRequested({ mayDiscardValue: false });
      }
    },
  },
  'fitmentSearch',
);

function sendRequestsIfNeeded(arg: EpicArg): void {
  const {
    action: {
      meta: {
        isSelectedFromDialog,
        isSelectedFromGarage,
        isSelectedFromVerifyFitment,
        sendIsolatedRequest: sendIsolatedRequestProp,
      } = {},
    },
  } = arg;

  if (
    (fitmentSearchConfig.needInitFitmentSearchRequest && !isSelectedFromVerifyFitment) ||
    isSelectedFromGarage
  ) {
    sendIsolatedRequest(arg, isolatedHeader);
  }

  if (sendIsolatedRequestProp && isSelectedFromDialog) {
    sendIsolatedRequest(arg, isolatedDialog);
  }

  if (fitmentSearchConfig.needInitVerifyFitmentRequest) {
    sendVerifyFitmentRequestFunc(arg);
  }
}

function sendIsolatedRequest(arg: EpicArg, isolatedKey: string | null, onInit?: boolean): void {
  const {
    state,
    dispatch,
    action: {
      meta: { extra = {}, responseMeta = {}, sendIsolatedRequest, vehicle } = {},
      type: actionType,
      payload,
    },
  } = arg;

  const preselection =
    fitmentSearchConfig.isOnCategorySelectionPage && isolatedKey === isolatedHeader
      ? requestConfig.localPreselection
      : [];

  // TODO: better to add a reaction to the vehicleSelected in isolatedHeader.request.selection
  // after the epic/state update ordering is untangled.
  const actionVehicle = actionType === vehicleSelected.type ? payload : vehicle;
  const fitmentSearchSelection = actionVehicle
    ? actionVehicle.selection
    : createFitmentSearchRequestSelectionSelector(isolatedKey)(state);

  const ignorePageContext = isolatedKey === isolatedDialog;

  const request = {
    selection: [...preselection, ...fitmentSearchSelection],
    pageSize: 0,
    mode: 'YMM',
    extra: {
      ...extra,
      ...(ignorePageContext && { ignorePageContext }),
    },
  };

  const createAbortController =
    isolatedKey === isolatedDialog ? createDialogAbortController : createHeaderAbortController;

  search(request, createAbortController())
    .then((response) => {
      if (response.State === 'cancelled') {
        return;
      }

      tryResponseRedirect(arg, response);

      if (responseMeta.isVehicleSelectionIsolated) {
        dispatch(
          setFitmentSearchResponse(
            response.Facets,
            responseMeta.isAutoVehicleSelectionDisabled,
            responseMeta.isPartialMode,
          ),
        );
      } else {
        dispatch(
          setResponse(response, {
            isolatedKey,
            onInit,
            sendIsolatedRequest,
            ...responseMeta,
          }),
        );
      }
    })
    .catch((err) => {
      logRuntimeError({ source: 'fitment_isolated_request', error: err });
    });
}

function sendVerifyFitmentRequestFunc(arg: EpicArg): void {
  const {
    state,
    dispatch,
    action: {
      meta: {
        extra: { vehicleJustDiscarded, vehicleJustSelected } = {},
        doNotRedirectOnVehicleSelect,
        isAutoVehicleSelectionDisabled,
        onInit,
        productId: customProductId,
        vehicle,
        variantId: customVariantId,
      } = {},
    },
  } = arg;

  const productId = customProductId ?? verifyFitmentProductIdSelector(state);

  if (customProductId) {
    dispatch(setVerifyFitmentProductId(customProductId));
  }

  if (
    !productId ||
    (arg.action.type === vehicleSelected.type && !arg.action.meta?.doNotRedirectOnVehicleSelect)
  ) {
    return;
  }

  const selection = vehicle ? vehicle.selection : verifyFitmentRequestSelectionSelector(state);
  const variantId = customVariantId || productConfig.getVariantId?.();

  const request = {
    selection,
    pageSize: 1,
    filterQuery: `${productConfig.fields.id}:${productId}`,
    mode: 'VerifyFitment',
    variantsMode: VariantsMode.Strip,
    extra: variantId ? { variantId } : {},
  };

  search(request, createVerifyAbortController())
    .then((response) => {
      if (response.State === 'cancelled') {
        return;
      }

      if (onInit) {
        fitmentSearchConfig.onVerifyFitmentInitResponseReceived?.(response);
      } else {
        fitmentSearchConfig.onVerifyFitmentResponseReceived?.(response);
      }

      const firstAction = response.Actions?.[0];
      if (
        firstAction?.Type === 'Redirect' &&
        firstAction.RedirectUrl &&
        window.location.pathname !== firstAction.RedirectUrl &&
        !doNotRedirectOnVehicleSelect
      ) {
        // Handle the VerifyFitment redirects here instead of with 'tryResponseRedirect' function.

        // 1. Ensure a vehicle is selected before redirecting.
        // If the new vehicle isn't selected, the previous vehicle will remain selected on the next page in the VerifyFitment widget.
        // This happens because this is the server redirect, and the new vehicle isn't included in the URL.
        // We use 'setTimeout' to delay the redirect, allowing time for the new vehicle to be selected first.
        const redirectUrl = firstAction.RedirectUrl;
        setTimeout(() => dispatch(replaceLocation(redirectUrl)), 0);
      }

      const fitmentFields = fitmentSearchFieldsSelector(state);
      const fitmentFacets = new FacetCollection(
        response.Facets?.filter((facet) => fitmentFields?.includes(facet.FieldName)) || [],
      );

      dispatch(
        setVerifyFitmentFacets(fitmentFacets, {
          isAutoVehicleSelectionDisabled:
            isAutoVehicleSelectionDisabled ?? fitmentSearchConfig.isAutoVehicleSelectionDisabled,
          onInit,
          vehicleJustDiscarded,
          vehicleJustSelected,
        }),
      );
      dispatch(setVerifyFitmentProduct(response.Items?.[0] ?? null));

      if (response.Extra) {
        dispatch(setResponseExtra(response.Extra));
      }

      tryAutoSelectVerifyFitmentVehicle(arg, fitmentFacets, fitmentFields);
    })
    .catch((err) => {
      logRuntimeError({ source: 'verify_fitment_request', error: err });
    });
}

function syncUrlWithVehicle(arg: EpicArg): void {
  // Since facet selection pages don't send search requests and don't use search state,
  // we need to manually add the vehicle to the URL
  // In this particular place we can add a vehicle from response. We can't do it everywhere.
  // Check this PR for more information https://github.com/Convermax/SiteSearch/pull/6827

  if (fitmentSearchConfig.isOnCategorySelectionPage) {
    const responseVehicleSelection = createResponseVehicleSelector(arg.action.meta?.isolatedKey ?? null)(
      arg.state,
    )?.selection;

    const shouldUpdateUrl =
      (!fitmentSearchConfig.hideVehicleFromUrl || fitmentSearchConfig.doNotSaveSelectedVehicle) &&
      responseVehicleSelection?.length;

    if (shouldUpdateUrl) {
      const newLocation = getUriFromRequest(
        { selection: responseVehicleSelection },
        { pathname: window.location.pathname },
      );
      arg.dispatch(replaceInHistory(newLocation, null));
    }
  }
}

function tryAutoSelectVehicle(arg: EpicArg): ReturnType<typeof vehicleSelected> | undefined {
  const {
    state,
    dispatch,
    action: {
      meta: {
        isAutoVehicleSelectionDisabled,
        isolatedKey,
        isPartialMode,
        isSelectedFromVehicleSpecs,
        onInit,
        sendIsolatedRequest,
        doNotRedirectOnVehicleSelect,
        redirectUrl,
        vehicleWidgetName,
        withCurrentVehicle,
      } = {},
    },
  } = arg;

  if (
    (isAutoVehicleSelectionDisabled || fitmentSearchConfig.isAutoVehicleSelectionDisabled) &&
    !isSelectedFromVehicleSpecs
  ) {
    return;
  }

  const selectedVehicle = selectedVehicleSelector(state);
  const responseVehicle = createResponseVehicleSelector(isolatedKey)(state);
  const allRequiredFieldsHaveSelection = createAllRequiredFieldsHaveSelectionSelector(isolatedKey)(state);

  const isSelectedVehicleNew =
    !selectedVehicle?.equals(responseVehicle) &&
    (!selectedVehicle?.isMoreSpecificThan(responseVehicle) || !allRequiredFieldsHaveSelection);

  const responseFitmentFacets = createFitmentSearchResponseFacetsSelector(isolatedKey)(state);
  const responseFitmentFacetsWithSelection = responseFitmentFacets?.filter((f) => !!f.selection?.length);
  const allResponseFitmentFacetsHaveSelection =
    responseFitmentFacetsWithSelection?.length === responseFitmentFacets?.length;

  if (!isSelectedVehicleNew) {
    if (isolatedKey === isolatedDialog && !withCurrentVehicle && allResponseFitmentFacetsHaveSelection) {
      dispatch(dialogClosed('VehicleWidgetDialog'));
    }

    return;
  }

  const isSpecifyDialogOpened = isSpecifyDialogOpenedSelector(state);
  const isVehicleSelectionForced = isVehicleSelectionForcedSelector(state);

  if (isPartialMode || (onInit && fitmentSearchConfig.isVehicleWidgetInPartialMode)) {
    const requiredFields = createRequiredFitmentSearchFieldsSelector(isolatedKey)(state);
    const extraFields = createFitmentSearchExtraFieldsSelector(isolatedKey)(state);

    const justChangedField = responseFitmentFacetsWithSelection?.at(
      responseFitmentFacetsWithSelection?.length - 1,
    )?.field;

    const isJustChangedExtraField =
      extraFields && justChangedField !== undefined && extraFields.includes(justChangedField);

    // do not save the vehicle with only one field in partial mode
    if (((requiredFields?.length ?? 0) > 1 && allRequiredFieldsHaveSelection) || isJustChangedExtraField) {
      fitmentSearchConfig.onVehicleSelected(responseVehicle.filteredFieldMap);

      return vehicleSelected(responseVehicle, {
        isolatedKey,
        isSpecifyDialogOpened,
        isVehicleSelectionForced,
        isVehicleAutoSelected: true,
        onInit,
        sendIsolatedRequest,
        doNotRedirectOnVehicleSelect,
        redirectUrl,
        vehicleWidgetName,
      });
    }
  }

  if (allResponseFitmentFacetsHaveSelection) {
    fitmentSearchConfig.onVehicleSelected(responseVehicle.filteredFieldMap);

    return vehicleSelected(responseVehicle, {
      isolatedKey,
      isSpecifyDialogOpened,
      isVehicleSelectionForced,
      isVehicleAutoSelected: true,
      onInit,
      sendIsolatedRequest,
      doNotRedirectOnVehicleSelect,
      redirectUrl,
      vehicleWidgetName,
    });
  }
}

function tryAutoSelectVerifyFitmentVehicle(
  arg: EpicArg,
  fitmentFacets: FacetCollection,
  fitmentFields: string[],
): void {
  const { isAutoVehicleSelectionDisabled } = arg.action.meta || {};
  if (isAutoVehicleSelectionDisabled || arg.action.type === vehicleSelected.type) {
    return;
  }

  const selectedVehicle = selectedVehicleSelector(arg.state);
  const responseVehicle = new Vehicle(fitmentFacets.selection, fitmentFields);

  const isResponseVehicleNew = !selectedVehicle.equals(responseVehicle);
  const isAllFacetsSelected = !fitmentFacets.filter((f) => !f.selection.length).length;

  if (isResponseVehicleNew && isAllFacetsSelected) {
    fitmentSearchConfig.onVehicleSelected(responseVehicle.filteredFieldMap);

    arg.dispatch(
      vehicleSelected(responseVehicle, {
        isSelectedFromVerifyFitment: true,
        isVehicleAutoSelected: true,
        sendIsolatedRequest: true,
      }),
    );
  }
}

function onGarageUpdated(arg: EpicArg): void {
  const { customerId } = window.Convermax;
  if (!customerId) {
    return;
  }

  const garage = garageDataSelector(arg.state).map((vehicle) => vehicle.filteredFieldMap);
  const selectedVehicle = selectedVehicleSelector(arg.state).filteredFieldMap;

  saveCustomerGarage(String(customerId), garage, selectedVehicle);
}
